/*
 * Fish Wishlist — Order Form
 *
 * Rows are rendered server-side by the CUSTOMER_WISHLIST page code, so this file only handles
 * interaction: tabs, search, sorting, quantity steppers, totals and add-to-cart.
 *
 * The Wishlist Page block injects that markup asynchronously via innerHTML, so initialisation
 * is driven by a MutationObserver rather than DOMContentLoaded.
 */
(function () {
  'use strict';

  function makeFormatter(moneyFormat) {
    return function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      return moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)))
        .replace('{{amount_with_apostrophe_separator}}', amount.replace('.', "'"));
    };
  }

  function rowQty(row) {
    var input = row.querySelector('.fw-qty-input');
    return input ? (parseInt(input.value, 10) || 0) : 0;
  }

  function rowPrice(row) {
    return parseInt(row.getAttribute('data-price') || '0', 10);
  }

  function wireRow(row, formatMoney, onChange) {
    var input    = row.querySelector('.fw-qty-input');
    var btnMinus = row.querySelector('.fw-qty-btn--minus');
    var btnPlus  = row.querySelector('.fw-qty-btn--plus');
    if (!input || !btnMinus || !btnPlus) return;

    var isAvailable = row.getAttribute('data-available') === 'true';
    var maxQty  = parseInt(row.getAttribute('data-max') || '0', 10);
    var minQty  = parseInt(input.getAttribute('data-min') || '1', 10);
    var stepQty = parseInt(input.getAttribute('data-step') || '1', 10);
    var totalEl = row.querySelector('[data-row-total]');

    function clamp(val, mn, mx) { return Math.min(Math.max(val, mn), mx); }

    function paintTotal() {
      var qty = rowQty(row);
      if (totalEl) totalEl.textContent = qty > 0 ? formatMoney(rowPrice(row) * qty) : '-';
    }

    function refresh() {
      var qty = rowQty(row);
      btnMinus.disabled = !isAvailable || qty <= 0;
      btnPlus.disabled  = !isAvailable || maxQty <= 0 || qty >= maxQty;
      paintTotal();
      onChange();
    }

    btnMinus.addEventListener('click', function () {
      var cur = rowQty(row);
      input.value = cur <= minQty ? 0 : clamp(cur - stepQty, 0, maxQty);
      refresh();
    });

    btnPlus.addEventListener('click', function () {
      var cur = rowQty(row);
      input.value = cur === 0 ? Math.min(minQty, maxQty) : clamp(cur + stepQty, minQty, maxQty);
      refresh();
    });

    // change fires on blur/enter — snap the typed value onto the allowed range there, so that
    // typing an intermediate digit isn't fought mid-keystroke.
    input.addEventListener('change', function () {
      var val = rowQty(row);
      if (val > 0 && val < minQty) val = minQty;
      input.value = clamp(val, 0, maxQty);
      refresh();
    });

    input.addEventListener('input', function () {
      paintTotal();
      onChange();
    });

    refresh();
  }

  function applySort(tbody, sortKey, sortDir) {
    var rows = Array.prototype.slice.call(tbody.querySelectorAll('.fw-order-form__row'));
    rows.sort(function (a, b) {
      var aVal = a.getAttribute('data-sort-' + sortKey) || '';
      var bVal = b.getAttribute('data-sort-' + sortKey) || '';
      if (sortKey === 'price') {
        return sortDir === 'asc' ? parseFloat(aVal) - parseFloat(bVal) : parseFloat(bVal) - parseFloat(aVal);
      }
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    var frag = document.createDocumentFragment();
    rows.forEach(function (r) { frag.appendChild(r); });
    tbody.appendChild(frag);
  }

  function updateSortHeaders(headers, activeKey, activeDir) {
    headers.forEach(function (th) {
      th.classList.remove('fw-th--asc', 'fw-th--desc');
      th.setAttribute('aria-sort', 'none');
      if (th.getAttribute('data-sort-key') === activeKey) {
        th.classList.add(activeDir === 'asc' ? 'fw-th--asc' : 'fw-th--desc');
        th.setAttribute('aria-sort', activeDir === 'asc' ? 'ascending' : 'descending');
      }
    });
  }

  function parseVariantIds(raw) {
    return (raw || '').split(',').map(function (id) { return id.trim(); }).filter(Boolean);
  }

  function initOrderForm(formEl) {
    var moneyFormat = formEl.getAttribute('data-money-format') || '${{amount}}';
    var formatMoney = makeFormatter(moneyFormat);

    var addBtn     = formEl.querySelector('[data-fw-add-btn]');
    var grandTotal = formEl.querySelector('[data-fw-grand-total]');
    var feedback   = formEl.querySelector('[data-fw-feedback]');
    var searchEl   = formEl.querySelector('[data-fw-search]');
    var tbody      = formEl.querySelector('[data-fw-tbody]');
    var emptyEl    = formEl.querySelector('[data-fw-panel-empty]');

    if (!addBtn || !grandTotal || !tbody) return;

    var allRows      = Array.prototype.slice.call(tbody.querySelectorAll('.fw-order-form__row'));
    var activeIds    = null;
    var activeSearch = '';
    var activeSortKey = null;
    var activeSortDir = 'asc';

    function recalcGrandTotal() {
      var total = 0, hasAny = false;
      allRows.forEach(function (row) {
        var qty = rowQty(row);
        if (qty > 0) { total += rowPrice(row) * qty; hasAny = true; }
      });
      grandTotal.textContent = formatMoney(total);
      addBtn.disabled = !hasAny;
    }

    function applyFilter() {
      var q = activeSearch.trim().toLowerCase();
      allRows.forEach(function (row) {
        var tabMatch    = !activeIds || activeIds[row.getAttribute('data-variant-id')] === true;
        var searchMatch = !q || (row.getAttribute('data-search-key') || '').indexOf(q) !== -1;
        row.classList.toggle('fw-row--hidden', !(tabMatch && searchMatch));
      });
      var anyVisible = !!tbody.querySelector('.fw-order-form__row:not(.fw-row--hidden)');
      if (emptyEl) emptyEl.style.display = anyVisible ? 'none' : 'block';
    }

    var tabs = Array.prototype.slice.call(formEl.querySelectorAll('[data-fw-tab]'));

    function activateTab(tabEl) {
      tabs.forEach(function (t) {
        var active = t === tabEl;
        t.classList.toggle('fw-tab--active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      activeIds = {};
      parseVariantIds(tabEl.getAttribute('data-fw-variant-ids')).forEach(function (id) {
        activeIds[id] = true;
      });
      applyFilter();
    }

    allRows.forEach(function (row) { wireRow(row, formatMoney, recalcGrandTotal); });

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () { activateTab(tab); });
    });

    var firstTab = formEl.querySelector('.fw-tab--active') || tabs[0];
    if (firstTab) activateTab(firstTab);

    if (searchEl) {
      searchEl.addEventListener('input', function () {
        activeSearch = this.value;
        applyFilter();
      });
    }

    var sortHeaders = Array.prototype.slice.call(formEl.querySelectorAll('[data-sort-key]'));
    sortHeaders.forEach(function (th) {
      th.addEventListener('click', function () {
        var key = th.getAttribute('data-sort-key');
        activeSortDir = activeSortKey === key ? (activeSortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        activeSortKey = key;
        applySort(tbody, activeSortKey, activeSortDir);
        updateSortHeaders(sortHeaders, activeSortKey, activeSortDir);
      });
    });

    function showFeedback(msg, type) {
      if (!feedback) return;
      feedback.textContent = msg;
      feedback.className = 'fw-order-form__feedback fw-feedback--' + type;
      clearTimeout(feedback._timer);
      feedback._timer = setTimeout(function () {
        feedback.className = 'fw-order-form__feedback';
      }, 5000);
    }

    addBtn.addEventListener('click', function () {
      var items = [];
      allRows.forEach(function (row) {
        var qty = rowQty(row);
        if (qty > 0) items.push({ id: parseInt(row.getAttribute('data-variant-id'), 10), quantity: qty });
      });
      if (items.length === 0) return;

      addBtn.disabled = true;
      addBtn.classList.add('fw-btn--loading');
      addBtn.textContent = 'Adding...';

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (d) { throw new Error(d.description || 'Error adding to cart'); });
          }
          return r.json();
        })
        .then(function () {
          showFeedback('Items added to cart!', 'success');
          allRows.forEach(function (row) {
            var input = row.querySelector('.fw-qty-input');
            if (input) input.value = 0;
            var total = row.querySelector('[data-row-total]');
            if (total) total.textContent = '-';
          });
          // Nudge whichever cart drawer the theme uses.
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:cart:update', { bubbles: true }));
          return fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart, bubbles: true }));
              document.querySelectorAll('[data-cart-count], .cart-count-bubble span[aria-hidden], .cart__count')
                .forEach(function (el) { el.textContent = cart.item_count; });
            })
            .catch(function () {});
        })
        .catch(function (err) {
          showFeedback(err.message || 'Something went wrong. Please try again.', 'error');
        })
        .finally(function () {
          addBtn.classList.remove('fw-btn--loading');
          addBtn.textContent = 'Add to Cart';
          recalcGrandTotal();
        });
    });

    recalcGrandTotal();
  }

  function tryInit() {
    document.querySelectorAll('[data-fw-order-form]:not([data-fw-init])').forEach(function (formEl) {
      formEl.setAttribute('data-fw-init', '1');
      initOrderForm(formEl);
    });
  }

  // The form mutates its own DOM, so coalesce observer callbacks into one check per frame.
  var pending = false;
  function scheduleInit() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () { pending = false; tryInit(); });
  }

  tryInit();
  new MutationObserver(scheduleInit).observe(document.documentElement, { childList: true, subtree: true });

})();
